import { getFragmentData, graphql, TotalModeEnum } from '@e-socna/data-layer';
import type { FragmentType } from '@e-socna/data-layer';

import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { formatAmount } from '../../../utils/format-amount.util';
import { formatPercent } from '../../../utils/format-percent.util';

import { KeyValueTable } from '../../KeyValueTable';

import { TotalMatrixTable } from './TotalMatrixTable';
import {
  computeTotalDifferences,
  isZeroTotal,
} from './utils/total-matrix.util';

const AUTOMATIC_RESULT_UNAVAILABLE_PLACEHOLDER = '—';

interface Props {
  readonly automaticResult:
    | FragmentType<typeof ServiceUsagesTotalCardCmp_Total_Fragment>
    | undefined
    | null;
  readonly result:
    | FragmentType<typeof ServiceUsagesTotalCardCmp_Total_Fragment>
    | undefined
    | null;
  readonly totalMode?: TotalModeEnum | null | undefined;
}

export const ServiceUsagesTotalCardCmp_Total_Fragment = graphql(`
  fragment ServiceUsagesTotalCardCmp_Total_Fragment on Total {
    discountAsAmount {
      value
      currencyId
    }
    discountAsPercentage
    discountedAmountExcludingVat {
      value
      currencyId
    }
    discountedAmountIncludingVat {
      value
      currencyId
    }
    undiscountedAmountExcludingVat {
      value
      currencyId
    }
    vatAsAmount {
      value
      currencyId
    }
    vatAsPercentage
  }
`);

export const useTotalEntries = (pResult: Props['result']) => {
  const { currenciesIdMap } = useReferencesContext();

  if (!pResult) {
    return null;
  }

  const result = getFragmentData(
    ServiceUsagesTotalCardCmp_Total_Fragment,
    pResult,
  );

  return [
    {
      id: 'undiscountedAmountExcludingVat',
      label: 'Montant HT',
      value: formatAmount(
        result.undiscountedAmountExcludingVat,
        currenciesIdMap,
      ),
      valueAlign: 'right',
    },
    {
      id: 'discountAsAmount',
      label: 'Remise',
      value: (
        <>
          {formatAmount(result.discountAsAmount, currenciesIdMap)} (
          {formatPercent(result.discountAsPercentage)})
        </>
      ),
      valueAlign: 'right',
    },
    {
      id: 'discountedAmountExcludingVat',
      label: 'Montant HT après remise',
      value: formatAmount(result.discountedAmountExcludingVat, currenciesIdMap),
      valueAlign: 'right',
    },
    {
      id: 'vatAsAmount',
      label: 'TVA',
      value: (
        <>
          {formatAmount(result.vatAsAmount, currenciesIdMap)} (
          {formatPercent(result.vatAsPercentage)})
        </>
      ),
      valueAlign: 'right',
    },
    {
      id: 'discountedAmountIncludingVat',
      label: 'Montant TTC',
      value: formatAmount(result.discountedAmountIncludingVat, currenciesIdMap),
      valueAlign: 'right',
    },
  ] as const;
};

export const TOTAL_UNAVAILABLE_MESSAGE =
  'Résultats du calcul total non disponibles';

export const Content = ({
  automaticResult,
  result,
  totalMode,
}: Readonly<Props>) => {
  const { currenciesIdMap } = useReferencesContext();
  const entries = useTotalEntries(result);
  const automaticEntries = useTotalEntries(automaticResult);

  if (!entries) {
    return TOTAL_UNAVAILABLE_MESSAGE;
  }

  const automaticTotal = automaticResult
    ? getFragmentData(ServiceUsagesTotalCardCmp_Total_Fragment, automaticResult)
    : null;

  if (totalMode !== TotalModeEnum.Manual || isZeroTotal(automaticTotal)) {
    return (
      <KeyValueTable ariaLabel="Calcul du total" dense entries={entries} />
    );
  }

  const automaticValueById = new Map(
    (automaticEntries ?? []).map(({ id, value }) => [id, value]),
  );

  const differences =
    result && automaticTotal
      ? computeTotalDifferences(
          getFragmentData(ServiceUsagesTotalCardCmp_Total_Fragment, result),
          automaticTotal,
        )
      : null;

  const rows = entries.map(({ id, label, value }) => {
    const difference = differences?.get(id);

    return {
      id,
      label,
      manualValue: value,
      automaticValue:
        automaticValueById.get(id) ?? AUTOMATIC_RESULT_UNAVAILABLE_PLACEHOLDER,
      differenceValue: difference
        ? formatAmount(difference, currenciesIdMap)
        : AUTOMATIC_RESULT_UNAVAILABLE_PLACEHOLDER,
    };
  });

  return (
    <TotalMatrixTable
      ariaLabel="Calcul du total"
      automaticColumnLabel="Auto"
      differenceColumnLabel="Différence"
      manualColumnLabel="Manuel"
      rows={rows}
    />
  );
};
